import { Button } from 'antd';
import { throttle } from 'lodash';
import React, { FC, useEffect, useMemo, useRef } from 'react';
import classlist from 'utils/classlist';
import style from './style.scss';

interface LoadMoreProps {
    className?: string;
    loading: boolean;
    error: Error | null;
    isEnd: boolean;
    isAuto: boolean;
    endText?: string;
    loadingText?: React.ReactNode;
    errorBtnText?: React.ReactNode;
    loadBtnText?: React.ReactNode;
    bottomThreshold?: number;
    showMoreBtn?: boolean;
    container?: HTMLElement;
    throttleTime?: number;
    onScrollToBottom(): void;
}

const LoadMore: FC<LoadMoreProps> = ({
    loading,
    error,
    isEnd,
    isAuto,
    className,
    showMoreBtn = true,
    container,
    endText,
    loadingText,
    errorBtnText,
    loadBtnText,
    bottomThreshold = 200,
    throttleTime = 50,
    onScrollToBottom
}) => {
    const onScrollRef = useRef<LoadMoreProps['onScrollToBottom']>(onScrollToBottom);

    onScrollRef.current = onScrollToBottom;

    const throttleFn = useMemo(
        () =>
            throttle(fn => {
                if (!loading && isAuto && !isEnd) {
                    const htmlElem = container || document.documentElement;
                    const scrollTop = htmlElem.scrollTop || document.body.scrollTop || window.pageYOffset;
                    const scrollHeight = htmlElem.scrollHeight;
                    const clientHeight = htmlElem.clientHeight;

                    if (scrollHeight - (scrollTop + clientHeight) < bottomThreshold) {
                        fn();
                    }
                }
            }, throttleTime),
        [loading, isAuto, isEnd, container, bottomThreshold, throttleTime]
    );

    useEffect(() => {
        const handleScroll = () => {
            throttleFn(onScrollRef.current);
        };

        if (!loading && isAuto && !error) {
            window.addEventListener('scroll', handleScroll, { passive: true });
        }

        if (!isAuto || error) {
            window.removeEventListener('scroll', handleScroll);
        }

        return () => window.removeEventListener('scroll', handleScroll);
    }, [loading, isAuto, error, throttleFn]);

    const render = () => {
        if (loading) {
            return <span className={style.info}>{loadingText || __('正在加载')}</span>;
        }

        if (error) {
            return (
                <Button className={style.btn} block onClick={onScrollToBottom}>
                    {errorBtnText || __('加载失败，点击重试')}
                </Button>
            );
        }

        if (showMoreBtn && isEnd) {
            return <span className={style.info}>{endText || __('没有更多数据了')}</span>;
        }

        if (!isAuto || showMoreBtn) {
            return (
                <Button className={style.btn} block onClick={onScrollToBottom}>
                    {loadBtnText || __('加载更多')}
                </Button>
            );
        }

        return null;
    };

    return <div className={classlist(style.more, className)}>{render()}</div>;
};

export default LoadMore;
